import { useState, useEffect, useCallback } from 'react';
import { fetchPatientFullDataOnce, fetchLeadProcessIds, fetchFutureSessionProcessIds } from '../../../../../components/psychologist/calendar/data';
import { fetchNoScheduleMap, type NoScheduleEntry } from '../../../../../utils/tallyNoSchedule';

export type PatientMissingEntry = {
  patientId?: string;
  patientName: string;
  email?: string;
  processId?: string;
  lastSessionDate?: string;
  totalSessions?: number;
};

export type PatientHandledEntry = PatientMissingEntry & { reason: string };

type UseMissingNextSessionResult = {
  patientsMissingNext: PatientMissingEntry[];
  patientsHandled: PatientHandledEntry[];
  missingNextModalOpen: boolean;
  openMissingNextModal: () => void;
  dismissMissingNextModal: () => void;
  resetForViewEntry: () => void;
};

export function useMissingNextSession(psychologistId?: string): UseMissingNextSessionResult {
  const [patientsMissingNext, setPatientsMissingNext] = useState<PatientMissingEntry[]>([]);
  const [patientsHandled, setPatientsHandled] = useState<PatientHandledEntry[]>([]);
  const [missingNextModalOpen, setMissingNextModalOpen] = useState(false);
  const [missingNextDismissedAt, setMissingNextDismissedAt] = useState(0);

  const openMissingNextModal = () => setMissingNextModalOpen(true);
  const dismissMissingNextModal = () => {
    setMissingNextModalOpen(false);
    setMissingNextDismissedAt(Date.now());
  };
  // Simula el comportamiento de remontaje que ocurre en /sessions al navegar
  // hacia esa ruta: resetea el timestamp de cierre para que el efecto de
  // auto-apertura se dispare de nuevo al entrar a la vista.
  const resetForViewEntry = useCallback(() => {
    setMissingNextDismissedAt(0);
  }, []);

  useEffect(() => {
    if (!psychologistId) return;
    let cancelled = false;

    async function load() {
      try {
        const psychologistEmail = (() => {
          try {
            const raw = sessionStorage.getItem('psi-session-data') || localStorage.getItem('psi-session-data');
            return raw ? (JSON.parse(raw)?.user?.email ?? '') : '';
          } catch { return ''; }
        })();

        const sinceISO = (() => {
          const d = new Date();
          d.setDate(d.getDate() - 90);
          return d.toISOString();
        })();

        // 1 solo fetch (compartido/cacheado con fetchPatientsOnce vía data.ts) en vez de
        // 2 llamados separados por status=withoutSession / status=inactive: los buckets
        // ya vienen calculados por el backend, se clasifica en memoria.
        const [fullData, noScheduleMap, leadProcessIds, futureSessionProcessIds] = await Promise.all([
          fetchPatientFullDataOnce(psychologistId ?? ''),
          psychologistEmail
            ? fetchNoScheduleMap(psychologistEmail, sinceISO)
            : Promise.resolve(new Map<string, NoScheduleEntry>()),
          fetchLeadProcessIds(psychologistId ?? ''),
          fetchFutureSessionProcessIds(psychologistId ?? '').catch(() => new Set<string>()),
        ]);

        if (cancelled) return;

        // status_buckets marca "withoutsession" con solo mirar si alguna vez tuvo sesiones
        // settled, así que un ex-paciente que volvió y está de nuevo en un estado del
        // segmento lead (ej. "Horarios ofrecidos") igual cae ahí. Se excluye explícitamente
        // por process_id contra el estado real, no por cantidad de sesiones históricas.
        // Mismo problema con "on_hold": completó el alta y tuvo sesiones, solo está en
        // pausa dentro de la ventana de 30 días — no es un caso de "sin próxima sesión"
        // real y no debe ofrecerle a la psicóloga el formulario de no agendamiento.
        // r.next_session_date es un valor cacheado en el proceso: si algún flujo de
        // agendamiento no lo recalculó, un paciente con sesión real futura (visible en el
        // calendario) puede colar acá igual — se descarta cruzando contra futureSessionProcessIds.
        const results = fullData.filter(
          (r) => (r.status_buckets?.includes('withoutsession') ?? false)
            && !(r.status_buckets?.includes('inactive') ?? false)
            && r.scheduling_eligible !== false
            && !r.next_session_date
            && !(r.process_id && leadProcessIds.has(r.process_id))
            && !(r.process_id && futureSessionProcessIds.has(r.process_id))
            && r.patient_status !== 'on_hold'
        );

        const rawList = results
          .map((r) => ({
            patientId: r.id as string | undefined,
            patientName: [r.name as string | null, r.surname as string | null].filter(Boolean).join(' ').trim() || 'Sin nombre',
            email: (r.email as string) ?? undefined,
            processId: (r.process_id as string | undefined) || (r.id as string | undefined),
            lastSessionDate: (r.last_session_date as string) ?? undefined,
            totalSessions: Number.isFinite(r.session_total) ? Number(r.session_total) : 0,
          }))
          .sort((a, b) => {
            if (!a.lastSessionDate) return 1;
            if (!b.lastSessionDate) return -1;
            return a.lastSessionDate.localeCompare(b.lastSessionDate);
          });

        const isHandled = (processId: string | undefined, lastSessionDate: string | undefined): boolean => {
          if (!processId) return false;
          const entry = noScheduleMap.get(processId);
          if (!entry) return false;
          if (!entry.submittedAt || !lastSessionDate) return true;
          // Comparación por instante real, no por texto: submittedAt viene con offset
          // local (-03:00) y lastSessionDate en UTC (Z) desde api-core — comparar los
          // strings crudos ordena mal cuando difieren en formato de huso horario.
          return new Date(entry.submittedAt).getTime() > new Date(lastSessionDate).getTime();
        };

        const filteredList = noScheduleMap.size > 0
          ? rawList.filter((p) => !isHandled(p.processId, p.lastSessionDate))
          : rawList;

        const handled: PatientHandledEntry[] = noScheduleMap.size > 0
          ? rawList
              .filter((p) => isHandled(p.processId, p.lastSessionDate))
              .map((p) => ({ ...p, reason: noScheduleMap.get(p.processId!)?.reason ?? '' }))
          : [];

        setPatientsHandled(handled);
        setPatientsMissingNext(filteredList);
        if (filteredList.length > 0 && Date.now() - missingNextDismissedAt > 60000) {
          setMissingNextModalOpen(true);
        }
      } catch {
        // No mostramos modal si falla
      }
    }

    load();
    return () => { cancelled = true; };
  }, [missingNextDismissedAt, psychologistId]);

  return {
    patientsMissingNext,
    patientsHandled,
    missingNextModalOpen,
    openMissingNextModal,
    dismissMissingNextModal,
    resetForViewEntry,
  };
}
